import { useState } from "react";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useGetGroupMemberQuery } from "../../api/groupsApi";
import { cls, useDialogPaperSx } from "../hatim/soft/SoftKit";
import { FamilyRow, TitleDialog, TitlesSkeleton } from "./TitlesPage";
import { TitleThumb } from "./titleParts";
import UserAvatar from "../../components/ui/UserAvatar";
import ConnectionState from "../../components/ui/ConnectionState";

/** Ünvan nesnesini pencere için hazırlar: aile ve (en yüksekte) kazanıldı. */
export function asDialogTitle(title, { earned } = {}) {
  if (!title) return null;
  const match = /^([a-z]+)\.\d+$/.exec(String(title.code || ""));
  return {
    ...title,
    family: title.family || (match ? match[1] : undefined),
    earned: earned === undefined ? title.earned : earned || Boolean(title.earned),
  };
}

/** Ailenin birimi (pencerede "5 / 7 gün"). */
function unitIn(titles, family) {
  return titles?.families?.find((f) => f.key === family)?.unit || "";
}

/**
 * En yüksek ünvan ve kazanılan sayısı: açık zeminli sabit özet. Ünvana
 * basınca aynı ünvan penceresi salt okunur açılır.
 */
function HighestStrip({ titles, onSelect }) {
  const { t } = useScriptMode();
  const highest = titles?.highest || null;
  const Wrapper = highest && onSelect ? "button" : "div";
  return (
    <Wrapper
      {...(Wrapper === "button"
        ? { type: "button", onClick: () => onSelect(asDialogTitle(highest, { earned: true })) }
        : {})}
      className={`shrink-0 w-full text-left px-5 py-3 flex items-center gap-3 ${cls.summary} ${
        Wrapper === "button" ? "hover:bg-terakkiBrandCloud dark:hover:bg-white/[0.06] transition-colors" : ""
      }`}
      data-highest-title
    >
      <TitleThumb
        title={highest}
        size={40}
        fallback={
          <span
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              highest ? "text-white" : "bg-terakkiBrandMist text-terakkiNeutral dark:bg-white/10 dark:text-white/70"
            }`}
            style={highest ? { backgroundColor: highest.color } : undefined}
          >
            <WorkspacePremiumIcon fontSize="small" aria-hidden />
          </span>
        }
      />
      <div className="min-w-0">
        <div className={`text-[15px] truncate ${cls.title}`}>{highest?.name || t("titles_none")}</div>
        <div className={`text-xs ${cls.muted}`}>{t("mt_earned_count", { count: titles?.earnedCount ?? 0 })}</div>
      </div>
    </Wrapper>
  );
}

/** Ailelerin rozet satırları; rozete basınca şartı açılır. */
export function TitleFamilies({ titles }) {
  const [selected, setSelected] = useState(null);
  const unitOf = (key) => titles?.families?.find((f) => f.key === key)?.unit || "";
  return (
    <>
      {(titles?.families || []).map((family) => (
        <FamilyRow
          key={family.key}
          family={family}
          onSelect={(title) => setSelected({ ...title, family: title.family || family.key })}
        />
      ))}
      <TitleDialog title={selected} unit={unitOf(selected?.family)} onClose={() => setSelected(null)} />
    </>
  );
}

/**
 * Üye detayındaki Ünvanlar (mobil MemberTitlesPanel): üyenin en yüksek
 * ünvanı ve her ailede kazandıkları. Kilitliler soluk görünür.
 */
export default function MemberTitlesPanel({ groupId, memberId }) {
  const { t } = useScriptMode();
  const { data, isLoading, isError, error, isFetching: refreshing, refetch } = useGetGroupMemberQuery(
    { groupId, memberId },
    { skip: !groupId || !memberId }
  );
  const titles = data?.data?.titles;
  const [selected, setSelected] = useState(null);

  if (isLoading) return <TitlesSkeleton />;
  if (isError || !titles) {
    return (
      <ConnectionState
        error={isError ? error : null}
        message={t("titles_load_error")}
        onRetry={() => refetch()}
        retrying={refreshing}
        compact
      />
    );
  }
  return (
    <div className="flex flex-col lg:h-full lg:min-h-0" data-member-titles={memberId}>
      <HighestStrip titles={titles} onSelect={setSelected} />
      <div className="lg:flex-1 lg:min-h-0 overflow-y-auto">
        <TitleFamilies titles={titles} />
      </div>
      <TitleDialog title={selected} unit={unitIn(titles, selected?.family)} onClose={() => setSelected(null)} />
    </div>
  );
}

/**
 * Sıralamadaki bir kardeşe basınca açılan künye (mobil GroupMemberModal):
 * dönem özeti ve ünvanları. range: sıralamanın dönemi.
 */
export function GroupMemberDialog({ groupId, memberId, range, onClose }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const open = Boolean(memberId);
  const { data, isFetching, isError, error, refetch: refetchMember } = useGetGroupMemberQuery(
    { groupId, memberId, startDate: range?.startDate, endDate: range?.endDate },
    { skip: !open || !groupId }
  );
  const detail = data?.data;
  const stats = detail?.stats;
  const tiles = [
    { Icon: TrendingUpIcon, value: `%${stats?.completionRate ?? 0}`, label: t("mt_completion") },
    { Icon: StarOutlineIcon, value: String(stats?.perfectDays ?? 0), label: t("mükemmel gün") },
    { Icon: LocalFireDepartmentOutlinedIcon, value: String(stats?.streak ?? 0), label: t("gün seri") },
    { Icon: TimerOutlinedIcon, value: String(stats?.focusMinutes ?? 0), label: t("dk") },
  ];
  const ready = detail && String(detail.member?.id) === String(memberId);
  const [selectedTitle, setSelectedTitle] = useState(null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: paperSx }}>
      <div className="flex items-center gap-3 px-5 pt-4 pb-3">
        {ready ? (
          // Altyazı: profil resmi ünvanı ya da en yüksek ünvan; basınca o ünvanın penceresi.
          <button
            type="button"
            data-highest-title-link
            disabled={!detail.titles?.highest}
            onClick={() => setSelectedTitle(asDialogTitle(detail.titles.highest, { earned: true }))}
            className="rounded-xl disabled:cursor-default"
          >
            <UserAvatar
              person={{ ...detail.member, title: detail.member.title || detail.titles?.highest || null }}
              size={40}
            />
          </button>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className={`text-[17px] truncate ${cls.title}`}>
            {ready ? `${detail.member.name || ""} ${detail.member.surname || ""}`.trim() : t("loading")}
          </div>
        </div>
        <IconButton size="small" onClick={onClose} aria-label={t("close")}>
          <CloseIcon fontSize="small" className={cls.muted} />
        </IconButton>
      </div>
      {isFetching && !ready ? (
        <TitlesSkeleton />
      ) : isError || !ready ? (
        <ConnectionState
          error={isError ? error : null}
          message={t("mt_load_error")}
          onRetry={() => refetchMember()}
          retrying={isFetching}
          compact
        />
      ) : (
        <div className="max-h-[70vh] overflow-y-auto" data-group-member={memberId}>
          {/* Dört sayı: kutusuz, ince çizgiyle ayrılmış tek bant */}
          <div className={`grid grid-cols-4 ${cls.summary} border-t border-terakkiBorder dark:border-white/[0.14]`}>
            {tiles.map(({ Icon, value, label }, index) => (
              <div
                key={label}
                className={`flex flex-col items-center gap-0.5 py-3 ${
                  index > 0 ? "border-l border-terakkiBorder dark:border-white/[0.14]" : ""
                }`}
              >
                <Icon sx={{ fontSize: 16 }} className={cls.accent} aria-hidden />
                <span className={`text-lg font-light ${cls.title}`}>{value}</span>
                <span className={`text-[11px] ${cls.muted}`}>{label}</span>
              </div>
            ))}
          </div>
          <TitleFamilies titles={detail.titles} />
        </div>
      )}
      <TitleDialog
        title={selectedTitle}
        unit={unitIn(detail?.titles, selectedTitle?.family)}
        onClose={() => setSelectedTitle(null)}
      />
    </Dialog>
  );
}
